import React, { useState } from 'react';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { SystemEntity, parseEntityRef } from '@backstage/catalog-model';
import { useGroups, useSystems } from '../../../hooks';
import {
  C4DiagramModel,
  C4DiagramEntity,
  C4DiagramRelation,
  toC4DiagramEntity,
} from '../C4DiagramCard/types';
// @ts-ignore
import * as uuid from 'uuid';
import { useApi } from '@backstage/core-plugin-api';
import { modelApiRef } from '../../../services/ModelClient';
import useAsync from 'react-use/esm/useAsync';
import { C4ContainerDiagramCard } from '../C4ContainerDiagramCard';

export const ContainerDiagramCard = () => {
  const { entity } = useEntity<SystemEntity>();
  const relatedSystems = useRelatedEntities(entity, { kind: 'System' });
  const relatedProviders = useRelatedEntities(entity, { kind: 'Provider' });
  const relatedGroups = useRelatedEntities(entity, { kind: 'Group' });
  const relatedComponents = useRelatedEntities(entity, { kind: 'Component' });
  const [ownedComponents, setOwnedComponents] = useState<
    Array<C4DiagramEntity>
  >([]);
  const [relatedEntities, setRelatedEntities] = useState<
    Array<C4DiagramEntity>
  >([]);
  const systems = useSystems();
  const groups = useGroups();
  const modelApi = useApi(modelApiRef);

  useAsync(async () => {
    let result = await modelApi.getSystemProductComponents(
      entity.metadata.namespace,
      entity.metadata.name,
    );

    setOwnedComponents(result || []);
    result = await modelApi.getRelatedEntities(
      entity.kind,
      entity.metadata.namespace,
      entity.metadata.name,
    );
    setRelatedEntities(result || []);
  });

  if (
    !relatedSystems.loading &&
    !relatedGroups.loading &&
    !relatedProviders.loading &&
    !relatedComponents.loading &&
    !systems.loading &&
    !groups.loading &&
    entity?.metadata
  ) {
    const model: C4DiagramModel = {
      type: 'container',
      context: toC4DiagramEntity(entity, 'context'),
      entities: [],
      relations: [],
    };

    model.context!.entities = [];

    // System Container Model
    const parentOfSystems = entity.relations
      ?.filter(it => it.type === 'parentOf')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'system')
      .map(it => it.name);
    if (parentOfSystems) {
      // Context Entities
      for (const system of parentOfSystems) {
        const relatedEntity = relatedSystems.entities?.find(
          entity => entity.metadata.name === system,
        );
        if (relatedEntity) {
          pushContextEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
        }
      }

      // Entities
      for (const system of parentOfSystems) {
        const childSystem = relatedSystems.entities?.find(
          entity => entity.metadata.name === system,
        );
        if (childSystem) {
          for (const relation of childSystem.relations
            ? childSystem.relations
            : []) {
            if (
              relation.type === 'dependsOn' &&
              parseEntityRef(relation.targetRef).kind === 'system'
            ) {
              const relatedEntity = systems.entities!.find(
                it =>
                  it.metadata.name === parseEntityRef(relation.targetRef).name,
              )!;
              const relationDependsOn = (
                childSystem.spec?.dependsOn as any[]
              )?.find(rel => rel.name === relatedEntity.metadata.name);
              const relationDependencyOf = (
                relatedEntity.spec?.dependencyOf as any[]
              )?.find(rel => rel.name === childSystem.metadata.name);
              const description = relationDependsOn?.metadata?.description
                ? relationDependsOn?.metadata?.description
                : relationDependencyOf?.metadata?.description;
              pushEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
              pushRelation(model, {
                id: uuid.v4(),
                source: childSystem.metadata.name,
                target: relatedEntity.metadata.name,
                text: description,
              });
            } else if (
              relation.type === 'usedBy' &&
              parseEntityRef(relation.targetRef).kind === 'group'
            ) {
              const relatedEntity = groups.entities!.find(
                it =>
                  it.metadata.name === parseEntityRef(relation.targetRef).name,
              )!;
              const rel = (childSystem.spec?.usedBy as any[])?.find(
                rel => rel.name === relatedEntity.metadata.name,
              );
              pushEntity(model, toC4DiagramEntity(relatedEntity));
              pushRelation(model, {
                id: uuid.v4(),
                source: relatedEntity.metadata.name,
                target: childSystem.metadata.name,
                text: rel?.metadata?.description,
              });
            }
          }
        }
      }
    }

    // Component Container Model
    const systemComponents = relatedComponents.entities?.filter(
      it => it.spec?.system === entity.metadata.name,
    );
    if (systemComponents) {
      for (const systemComponent of systemComponents) {
        pushContextEntity(model, toC4DiagramEntity(systemComponent, 'context'));
        for (const relation of systemComponent.relations
          ? systemComponent.relations
          : []) {
          const target = parseEntityRef(relation.targetRef);
          if (target.kind === 'component') {
            const targetComponent = systemComponents.find(
              it => it.metadata.name === target.name,
            );
            const rel = (
              systemComponent.spec?.dependsOnComponents as any[]
            )?.find(rel => rel.name === target.name);
            if (targetComponent && relation.type === 'dependsOn') {
              pushRelation(model, {
                id: uuid.v4(),
                source: systemComponent.metadata.name,
                target: targetComponent.metadata.name,
                text: rel?.metadata?.description,
              });
            }
          } else if (target.kind === 'group') {
            const relatedEntity = relatedGroups.entities?.find(
              it => it.metadata.name === target.name,
            );
            if (relatedEntity && relation.type === 'usedBy') {
              const rel = (systemComponent.spec?.usedBy as any[])?.find(
                rel => rel.name === target.name,
              );
              pushEntity(model, toC4DiagramEntity(relatedEntity));
              pushRelation(model, {
                id: uuid.v4(),
                source: relatedEntity.metadata.name,
                target: systemComponent.metadata.name,
                text: rel?.metadata?.description,
              });
            }
          } else if (target.kind === 'system') {
            const relatedEntity = relatedSystems.entities?.find(
              it => it.metadata.name === target.name,
            );
            if (relatedEntity && relation.type === 'dependsOn') {
              const rel = (
                systemComponent.spec?.dependsOnSystems as any[]
              )?.find(rel => rel.name === target.name);
              pushEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
              pushRelation(model, {
                id: uuid.v4(),
                source: systemComponent.metadata.name,
                target: relatedEntity.metadata.name,
                text: rel?.metadata?.description,
              });
            } else if (relatedEntity && relation.type === 'dependencyOf') {
              const rel = (
                systemComponent.spec?.dependencyOfSystems as any[]
              )?.find(rel => rel.name === target.name);
              pushEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
              pushRelation(model, {
                id: uuid.v4(),
                source: relatedEntity.metadata.name,
                target: systemComponent.metadata.name,
                text: rel?.metadata?.description,
              });
            }
          } else if (target.kind === 'provider') {
            const relatedEntity = relatedProviders.entities?.find(
              it => it.metadata.name === target.name,
            );
            if (relatedEntity && relation.type === 'dependsOn') {
              const rel = (
                systemComponent.spec?.dependsOnProviders as any[]
              )?.find(rel => rel.name === target.name);
              pushEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
              pushRelation(model, {
                id: uuid.v4(),
                source: systemComponent.metadata.name,
                target: relatedEntity.metadata.name,
                text: rel?.metadata?.description,
              });
            }
          }
        }
      }
    }
    return (
      <C4ContainerDiagramCard
        model={model}
        components={ownedComponents}
        relatedEntities={relatedEntities}
      />
    );
  }
  return <></>;
};

function pushEntity(model: C4DiagramModel, entity: C4DiagramEntity) {
  if (
    !model.entities.find(it => it.id === entity.id) &&
    !model.context?.entities?.find(it => it.id === entity.id)
  ) {
    model.entities.push(entity);
  }
}

function pushContextEntity(model: C4DiagramModel, entity: C4DiagramEntity) {
  if (
    !model.entities.find(it => it.id === entity.id) &&
    !model.context?.entities?.find(it => it.id === entity.id)
  ) {
    model.context?.entities?.push(entity);
  }
}

function pushRelation(model: C4DiagramModel, relation: C4DiagramRelation) {
  if (
    !model.relations.find(
      it => it.source === relation.source && it.target == relation.target,
    )
  ) {
    model.relations.push(relation);
  }
}
